import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { RecipeCard, RecipeCardSkeleton } from '../components/RecipeCard';
import { Heart, ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FavoritesPage() {
  const { t, user, login } = useAuth();
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchFavorites = async () => {
      try {
        const response = await fetch(`${API_URL}/api/favorites`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setRecipes(data.recipes || []);
          setFavorites(new Set(data.favorites.map(f => f.recipe_id)));
        }
      } catch (error) {
        console.error('Failed to fetch favorites:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFavorites();
  }, [user]);

  const toggleFavorite = async (recipeId) => {
    if (!user) return;

    try {
      await fetch(`${API_URL}/api/favorites/${recipeId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setFavorites(prev => {
        const next = new Set(prev);
        next.delete(recipeId);
        return next;
      });
      setRecipes(prev => prev.filter(r => r.recipe_id !== recipeId));
    } catch (error) {
      console.error('Failed to remove favorite:', error);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-cream-subtle flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Heart className="w-10 h-10 text-primary" />
          </div>
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-text-heading mb-4">
            {t('favorites')}
          </h1>
          <p className="text-muted-foreground mb-6">
            {t('loginWithGoogle')} pre zobrazenie vašich obľúbených receptov.
          </p>
          <Button
            onClick={login}
            className="bg-primary hover:bg-primary-hover text-white rounded-full px-8"
          >
            {t('login')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-subtle py-8" data-testid="favorites-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-full"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-bold text-text-heading">
              {t('favorites')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {recipes.length} {recipes.length === 1 ? 'recept' : recipes.length < 5 ? 'recepty' : 'receptov'}
            </p>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <RecipeCardSkeleton key={i} />
            ))}
          </div>
        ) : recipes.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
              <Heart className="w-10 h-10 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground text-lg mb-4">
              Zatiaľ nemáte žiadne obľúbené recepty.
            </p>
            <Link to="/recipes">
              <Button className="bg-primary hover:bg-primary-hover text-white rounded-full px-8">
                Objaviť recepty
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {recipes.map((recipe) => (
              <RecipeCard
                key={recipe.recipe_id}
                recipe={recipe}
                isFavorite={favorites.has(recipe.recipe_id)}
                onToggleFavorite={toggleFavorite}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
